import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetUser } from '@sib/api';
import { useLogout } from '@sib/auth';
import { tracking } from '@sib/tracking';
import { Button, Tooltip } from '@sib/ui';
import { Stars } from '@sib/ui/icons';
import { ActivateAiModal } from '..';
import { useAskAi } from '../hooks';
import { updateUserId } from '../utils';
import { AskAiModal } from './AskAiModal';
import { LogoutButton } from './LogoutButton';
import { NavigationMenu } from './NavigationMenu';
import { UserWelcomeCard } from './UserWelcomeCard';

export const NavContent = () => {
  const { mutate: logout } = useLogout({ onLogout: updateUserId });
  const { data: identity } = useGetUser();
  const { t } = useTranslation();
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTextTruncated, setIsTextTruncated] = useState(false);

  const {
    isAiModalOpen,
    isAskAiModalOpen,
    organization,
    customBrandName,
    handleAskAi,
    handleCloseAiModal,
    handleCloseAskAiModal,
  } = useAskAi();

  const buttonLabel = customBrandName || t('askSpendBrain');

  useEffect(() => {
    if (!identity) return;
    tracking.identifyUser(identity?.email, identity?.name);
  }, [identity]);

  useEffect(() => {
    const element = textRef.current;
    if (!element) return;

    setIsTextTruncated(element.scrollWidth > element.clientWidth);
  }, [buttonLabel]);

  return (
    <nav className="flex h-full flex-col border-r">
      <div className="flex items-center justify-center px-2">
        <UserWelcomeCard
          email={identity?.email || ''}
          name={identity?.name || ''}
          avatarUrl={identity?.avatarUrl || ''}
        />
      </div>

      <div className="flex items-center justify-center px-2 py-2">
        <Tooltip content={isTextTruncated ? buttonLabel : null}>
          <Button
            variant="secondary"
            className="max-w-[230px] px-2"
            onClick={handleAskAi}
          >
            <span className="flex min-w-0 items-center gap-2">
              <Stars className="h-5 shrink-0" />
              <span ref={textRef} className="truncate">
                {buttonLabel}
              </span>
            </span>
          </Button>
        </Tooltip>
      </div>

      <div className="flex flex-1 flex-col justify-between overflow-x-hidden overflow-y-auto overscroll-contain">
        <div className="w-full pt-2">
          <NavigationMenu />
        </div>
        <div className="flex flex-col px-0.5 pb-10">
          <LogoutButton onLogout={logout} />
          <div className="flex w-full items-center justify-center py-5 pb-1">
            <p className="text-muted-foreground justify-center text-[10px]">
              {`${t('appName')} v${__APP_VERSION__}`}
            </p>
          </div>
        </div>
      </div>

      <ActivateAiModal
        isOpen={isAiModalOpen}
        onClose={handleCloseAiModal}
        organization={organization}
      />
      <AskAiModal
        isOpen={isAskAiModalOpen}
        onClose={handleCloseAskAiModal}
        title={buttonLabel}
      />
    </nav>
  );
};
